import {
  DelegateV1,
  DelegateV2,
  V1Delegation,
  V2Delegation,
} from '@delegatexyz/sdk';
import { http } from 'viem';

import {
  baseDelegatedContractAddresses,
  ethereumDelegatedContractAddresses,
} from './delegated-contract-mapping';
import { getContractNameFromAddress } from './get-contract-name-from-address';

const ethereumConfig = {
  urlName:
    process.env.NEXT_PUBLIC_APP_ENV === 'production'
      ? 'eth-mainnet'
      : 'eth-sepolia',
};

const baseConfig = {
  urlName:
    process.env.NEXT_PUBLIC_APP_ENV === 'production'
      ? 'base-mainnet'
      : 'base-sepolia',
};

const ETH_RPC_URL = `https://${ethereumConfig.urlName}.g.alchemy.com/v2/${
  process.env.NEXT_PUBLIC_ALCHEMY_API_KEY as string
}`;

const BASE_RPC_URL = `https://${baseConfig.urlName}.g.alchemy.com/v2/${
  process.env.NEXT_PUBLIC_ALCHEMY_API_KEY as string
}`;

export const getDelegatedTokenIdsForAddresses = async (
  addresses: string[],
): Promise<{
  delegatedTokens: {
    type: string;
    tokenId: string;
    contractName: string;
    owner: string;
  }[];
  delegatedAddresses: string[];
  delegatedContractAddresses: {
    contractName: string;
    walletAddress: string;
  }[];
}> => {
  try {
    const ethereumDelegatev1 = new DelegateV1(http(ETH_RPC_URL));
    const baseDelegatev1 = new DelegateV1(http(BASE_RPC_URL));
    const delegatedAddresses = [];
    const delegatedTokens = [];
    const delegatedContractAddresses = [];

    const ethereumDelegatev2 = new DelegateV2({
      userTransport: http(ETH_RPC_URL),
    });
    const baseDelegatev2 = new DelegateV2({
      userTransport: http(BASE_RPC_URL),
    });

    const datav1: V1Delegation[] = [];
    const datav2: V2Delegation[] = [];
    for (const address of addresses) {
      // For V1
      const ethdatav1 = await ethereumDelegatev1.getDelegationsByDelegate(
        address as `0x${string}`,
      );

      const basedatav1 = await baseDelegatev1.getDelegationsByDelegate(
        address as `0x${string}`,
      );

      // For V2
      const ethdatav2 = await ethereumDelegatev2.getIncomingDelegations(
        address as `0x${string}`,
      );

      const basedatav2 = await baseDelegatev2.getIncomingDelegations(
        address as `0x${string}`,
      );

      datav1.push(...ethdatav1, ...basedatav1);
      datav2.push(...ethdatav2, ...basedatav2);
    }

    for (const V1delegation of datav1) {
      console.log('V1delegation checking for ', V1delegation);
      if (V1delegation.type === 'ALL') {
        delegatedAddresses.push(V1delegation.vault);
      } else {
        const isValidContract =
          ethereumDelegatedContractAddresses.includes(
            V1delegation.contract?.toLowerCase(),
          ) ||
          baseDelegatedContractAddresses.includes(
            V1delegation.contract?.toLowerCase(),
          );

        if (!isValidContract) {
          console.error('Invalid contract', V1delegation.contract);
          continue;
        }

        const contractName = getContractNameFromAddress(V1delegation.contract);

        if (V1delegation.type === 'CONTRACT') {
          delegatedContractAddresses.push({
            contractName,
            walletAddress: V1delegation.vault,
          });
        } else if (
          (contractName === 'Pass' ||
            contractName === 'DoodlesCertifiedViral') &&
          V1delegation.type != 'CONTRACT'
        ) {
          const tokenId = `${contractName}-${V1delegation.vault.toLowerCase()}-${V1delegation.tokenId}`;
          delegatedTokens.push({
            type: V1delegation.type,
            tokenId,
            contractName,
            owner: V1delegation.vault,
          });
        } else {
          const tokenId = `${contractName}-${V1delegation.tokenId}`;
          delegatedTokens.push({
            type: V1delegation.type,
            tokenId,
            contractName,
            owner: V1delegation.vault,
          });
        }
      }
    }

    for (const V2delegation of datav2) {
      console.log('V2delegation checking for ', V2delegation);
      if (V2delegation.type === 'ALL') {
        delegatedAddresses.push(V2delegation.from);
      } else {
        const isValidContract =
          ethereumDelegatedContractAddresses.includes(
            V2delegation.contract?.toLowerCase(),
          ) ||
          baseDelegatedContractAddresses.includes(
            V2delegation.contract?.toLowerCase(),
          );

        if (!isValidContract) {
          console.error('Invalid contract', V2delegation.contract);
          continue;
        }

        const contractName = getContractNameFromAddress(V2delegation.contract);

        if (V2delegation.type === 'CONTRACT') {
          delegatedContractAddresses.push({
            contractName,
            walletAddress: V2delegation.from,
          });
        } else if (V2delegation.type === 'ERC1155') {
          const tokenId = `${contractName}-${V2delegation.from.toLowerCase()}-${V2delegation.tokenId}`;
          delegatedTokens.push({
            type: V2delegation.type,
            tokenId,
            contractName,
            owner: V2delegation.from,
          });
        } else {
          const tokenId = `${contractName}-${V2delegation.tokenId}`;
          delegatedTokens.push({
            type: V2delegation.type,
            tokenId,
            contractName,
            owner: V2delegation.from,
          });
        }
      }
    }

    return {
      delegatedTokens: [...new Set(delegatedTokens)],
      delegatedAddresses: [...new Set(delegatedAddresses)],
      delegatedContractAddresses: [...new Set(delegatedContractAddresses)],
    };
  } catch (e) {
    console.log(e);
  }

  return {
    delegatedTokens: [],
    delegatedAddresses: [],
    delegatedContractAddresses: [],
  };
};
